export type ConnectorType = "type1" | "type2" | "ccs" | "chademo" | "tesla" | "gbt" | "nacs";

// AC (slow) connectors. Everything else is DC fast charging.
const SLOW_CONNECTORS: ReadonlySet<ConnectorType> = new Set<ConnectorType>(["type1", "type2"]);

export function isFastConnector(type: ConnectorType): boolean {
  return !SLOW_CONNECTORS.has(type);
}

export const CONNECTOR_LABEL: Record<ConnectorType, string> = {
  type1: "J1772",
  type2: "Type 2",
  ccs: "CCS",
  chademo: "CHAdeMO",
  tesla: "Tesla",
  gbt: "GB/T",
  nacs: "NACS",
};

export function formatKw(kw: number): string {
  return Number.isInteger(kw) ? `${kw}` : kw.toFixed(1);
}

export type PlugGroup = {
  connector_type: ConnectorType;
  power_kw: number;
  count: number;
};

export function groupPlugs(
  chargers: ReadonlyArray<{ connector_type: ConnectorType; power_kw: number }>,
): PlugGroup[] {
  const byKey = new Map<string, PlugGroup>();
  for (const c of chargers) {
    const key = `${c.connector_type}|${c.power_kw}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      byKey.set(key, { connector_type: c.connector_type, power_kw: c.power_kw, count: 1 });
    }
  }
  return sortPlugGroups([...byKey.values()]);
}

// Fast-first so drivers scanning a chip row see usable plugs before trickle.
export function sortPlugGroups<T extends { connector_type: ConnectorType; power_kw: number }>(
  groups: T[],
): T[] {
  return [...groups].sort((a, b) => {
    const aFast = isFastConnector(a.connector_type) ? 0 : 1;
    const bFast = isFastConnector(b.connector_type) ? 0 : 1;
    if (aFast !== bFast) return aFast - bFast;
    if (b.power_kw !== a.power_kw) return b.power_kw - a.power_kw;
    return a.connector_type.localeCompare(b.connector_type);
  });
}
